import {
  BulkWindowBlockRequest as BulkWindowBlockRequestMsg,
  BulkWindowBlockSlot as BulkWindowBlockSlotMsg,
  type CallWindow,
  type Event,
  Event as EventMsg,
  type EventList,
} from '@kalos/kalos-rpc';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addDays, format, parseISO } from 'date-fns';
import { useCallback, useMemo } from 'react';

import { EventClientService } from '../../tools/helpers';
import { queryKeys } from './constants';

export interface WindowBlockParams {
  departmentId: number;
  dateStarted: string;
  timeStarted: string;
  timeEnded: string;
  description?: string;
  jobTypeId?: number;
}

/**
 * Fetch window blocks for a date range via BatchGetReminders.
 * When departmentId is provided, filters server-side to reduce payload.
 */
export const useWindowBlocksQuery = ({
  dateStart,
  dateEnd,
  departmentId,
  enabled = true,
}: {
  dateStart: string;
  dateEnd: string;
  departmentId?: number;
  enabled?: boolean;
}) => {
  return useQuery<EventList>({
    queryKey: [
      queryKeys.windowBlock.root,
      queryKeys.windowBlock.list,
      dateStart,
      dateEnd,
      departmentId ?? null,
    ],
    queryFn: async () => {
      const req = EventMsg.create({
        isActive: 1,
        isWindowBlock: 1,
        withoutLimit: true,
        dateRange: ['>=', dateStart, '<=', dateEnd],
        ...(departmentId ? { departmentId } : {}),
      });
      return await EventClientService.BatchGetReminders(req);
    },
    enabled: enabled && !!dateStart && !!dateEnd,
  });
};

export const useWindowBlockCreateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: WindowBlockParams) => {
      const req = EventMsg.create({
        propertyId: 0,
        departmentId: params.departmentId,
        dateStarted: params.dateStarted,
        dateEnded: params.dateStarted,
        timeStarted: params.timeStarted,
        timeEnded: params.timeEnded,
        name: 'Window Block',
        description: params.description ?? '',
        isActive: 1,
        isWindowBlock: 1,
        ...(params.jobTypeId ? { jobTypeId: params.jobTypeId } : {}),
      });
      return await EventClientService.Create(req);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [queryKeys.windowBlock.root] }),
        queryClient.invalidateQueries({ queryKey: [queryKeys.events.root] }),
      ]);
    },
  });
};

export const useWindowBlockDeleteMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (event: Event) => {
      return await EventClientService.Delete(event);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [queryKeys.windowBlock.root] }),
        queryClient.invalidateQueries({ queryKey: [queryKeys.events.root] }),
      ]);
    },
  });
};

export const useWindowBlockDeleteManyMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (events: Event[]) => {
      for (const event of events) {
        await EventClientService.Delete(event);
      }
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [queryKeys.windowBlock.root] }),
        queryClient.invalidateQueries({ queryKey: [queryKeys.events.root] }),
      ]);
    },
  });
};

export const useWindowBlockBulkCreateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      dates,
      departmentIds,
      jobTypeIds,
      callWindows,
    }: {
      dates: string[];
      departmentIds: number[];
      jobTypeIds: number[];
      callWindows: CallWindow[];
    }) => {
      const slots = callWindows.map((w) =>
        BulkWindowBlockSlotMsg.create({
          dayOfWeek: w.dayOfWeek,
          startTime: w.startTime,
          endTime: w.endTime,
          departmentId: w.departmentId,
        }),
      );

      const req = BulkWindowBlockRequestMsg.create({
        departmentIds,
        jobTypeIds,
        dateStart: dates[0],
        dateEnd: dates[dates.length - 1],
        callWindows: slots,
      });

      await EventClientService.BulkCreateWindowBlocks(req);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [queryKeys.windowBlock.root] }),
        queryClient.invalidateQueries({ queryKey: [queryKeys.events.root] }),
      ]);
    },
  });
};

/**
 * Returns the jobTypeIds of all blocks matching the given time window.
 */
const normalizeTime = (t: string): string => {
  if (!t) return '';
  const [h = '0', m = '00'] = t.split(':');
  return `${h.padStart(2, '0')}:${m.slice(0, 2).padStart(2, '0')}`;
};

export function getBlockedJobTypesForWindow(
  blocks: Event[],
  timeStarted: string,
  timeEnded: string,
): number[] {
  const ids = blocks
    .filter(
      (block) =>
        normalizeTime(block.timeStarted) === normalizeTime(timeStarted) &&
        normalizeTime(block.timeEnded) === normalizeTime(timeEnded),
    )
    .map((block) => block.jobTypeId);
  return [...new Set(ids)];
}

/**
 * Returns true if the window is fully blocked — either a legacy all-type block
 * (jobTypeId === 0) or every job type has its own block.
 */
export function isFullyBlocked(
  blocks: Event[],
  timeStarted: string,
  timeEnded: string,
  totalJobTypeCount: number,
): boolean {
  const blockedTypes = getBlockedJobTypesForWindow(blocks, timeStarted, timeEnded);
  if (blockedTypes.some((id) => id === 0)) return true;
  return totalJobTypeCount > 0 && blockedTypes.length >= totalJobTypeCount;
}

/**
 * Check if a specific call window is blocked on a given date.
 */

export function isWindowBlocked(
  windowBlocks: Event[],
  timeStarted: string,
  timeEnded: string,
): boolean {
  return windowBlocks.some(
    (block) =>
      normalizeTime(block.timeStarted) === normalizeTime(timeStarted) &&
      normalizeTime(block.timeEnded) === normalizeTime(timeEnded),
  );
}

/**
 * Get window blocks for a specific date from the fetched block list.
 */
export function getBlocksForDate(blocks: Event[], date: string): Event[] {
  return blocks.filter((b) => b.dateStarted.startsWith(date));
}

/**
 * Combined hook for block/unblock toggle with spinner-safe state.
 * Each component instance gets its own query, so `isFetching` tracks
 * its own refetch cycle — preventing the spinner from dropping early.
 */
export const useWindowBlockToggle = ({
  date,
  departmentId,
  startTime,
  endTime,
  enabled = true,
}: {
  date: string;
  departmentId?: number;
  startTime?: string;
  endTime?: string;
  enabled?: boolean;
}) => {
  const dateEnd = format(addDays(parseISO(date), 1), 'yyyy-MM-dd');
  const blocksQuery = useWindowBlocksQuery({
    dateStart: date,
    dateEnd,
    departmentId,
    enabled,
  });
  const createMut = useWindowBlockCreateMutation();
  const deleteManyMut = useWindowBlockDeleteManyMutation();

  const windowBlocks = useMemo(() => blocksQuery.data?.results ?? [], [blocksQuery.data]);
  const isBlocked =
    startTime && endTime ? isWindowBlocked(windowBlocks, startTime, endTime) : false;
  const isBusy = createMut.isPending || deleteManyMut.isPending || blocksQuery.isFetching;

  const toggle = useCallback(() => {
    if (!startTime || !endTime) return;
    const matchingBlocks = windowBlocks.filter(
      (b) =>
        normalizeTime(b.timeStarted) === normalizeTime(startTime) &&
        normalizeTime(b.timeEnded) === normalizeTime(endTime),
    );
    if (matchingBlocks.length > 0) {
      deleteManyMut.mutate(matchingBlocks);
    } else {
      createMut.mutate({
        departmentId: departmentId ?? 0,
        dateStarted: date,
        timeStarted: startTime,
        timeEnded: endTime,
      });
    }
  }, [windowBlocks, startTime, endTime, departmentId, date, createMut, deleteManyMut]);

  return { isBlocked, isBusy, toggle, windowBlocks };
};
